import { useTheme } from '@mui/material';

import { Icons, EntityAction, EntityActions } from '@cast/design-system';

import { useAbility } from '@cast/shared-ability-data-access';

import {
  useDeleteEdgeLocationDialog,
  useEditEdgeLocationDrawer,
  useOffboardEdgeLocationDialog,
} from '../_hooks';
import { CUSTOM_EDGE_PROVIDER } from '../constants';
import { EdgeLocationMappedState, EnhancedEdgeLocation } from '../types';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const getTooltipTitle = (
  hasEdges: boolean,
  isInUse: boolean
): string | null => {
  if (hasEdges && isInUse) {
    return 'Cannot delete edge locations that are assigned to node templates and have provisioned nodes.';
  } else if (hasEdges) {
    return 'Cannot delete edge locations that have provisioned nodes.';
  } else if (isInUse) {
    return 'Cannot delete edge locations that are assigned to node templates.';
  }

  return null;
};

type EdgeLocationActionsProps = {
  edgeLocation: EnhancedEdgeLocation;
};

export const EdgeLocationActions = ({
  edgeLocation,
}: EdgeLocationActionsProps) => {
  const { palette } = useTheme();
  const [canEdit, canDelete] = useAbility().canMany([
    ['update', 'ClusterEdgeLocations'],
    ['delete', 'ClusterEdgeLocations'],
  ]);

  const { cluster } = useCluster();
  const { openEditEdgeLocationDrawer } = useEditEdgeLocationDrawer();
  const { openDeleteEdgeLocationDialog } = useDeleteEdgeLocationDialog();
  const { openDialog: openOffboardEdgeLocationDialog } =
    useOffboardEdgeLocationDialog();

  const handleDeleteClick = () => {
    if (
      edgeLocation.mappedState === EdgeLocationMappedState.Pending ||
      edgeLocation.provider === CUSTOM_EDGE_PROVIDER
    ) {
      openDeleteEdgeLocationDialog({
        edgeLocation,
        clusterId: cluster.id,
      });
      return;
    }

    openOffboardEdgeLocationDialog({
      edgeLocation,
      clusterId: cluster.id,
    });
  };

  const renderDeleteAction = () => {
    const hasEdges = !!edgeLocation.totalEdgeCount;
    const isInUse = edgeLocation.mappedState === EdgeLocationMappedState.InUse;
    const deleteActionDisabled = !canDelete || hasEdges || isInUse;

    return (
      <EntityAction
        testId="edge-location-delete-action"
        startAdornment={<Icons.Trash />}
        disruptive
        disabled={deleteActionDisabled}
        onClick={handleDeleteClick}
        tooltipProps={
          deleteActionDisabled
            ? { title: getTooltipTitle(hasEdges, isInUse), placement: 'bottom' }
            : undefined
        }
      >
        Delete
      </EntityAction>
    );
  };

  return (
    <EntityActions>
      <EntityAction
        testId="edge-location-edit-action"
        startAdornment={<Icons.PencilSimple color={palette.grey[900]} />}
        disabled={!canEdit}
        onClick={() => {
          openEditEdgeLocationDrawer({
            edgeLocation,
            clusterId: cluster.id,
          });
        }}
      >
        Edit
      </EntityAction>
      {renderDeleteAction()}
    </EntityActions>
  );
};
